import { token } from "@/styled-system/tokens";
import { refinance } from "@zillow/mortgage-calculations";
import { useFormContext } from "react-hook-form";
import { useLastValidValues } from "../../utils/form/useLastValidValues.esm.js";
import { COMMON_PERCENT_CONFIG, COMMON_AMOUNT_CONFIG, getIsNanError, castAsNumberIfNotNan } from "../../utils/zhl-calculator.esm.js";
const COMMON_TERM_CONFIG = {
  setValueAs: castAsNumberIfNotNan,
  max: {
    value: 500,
    message: `Must be 500 at most.`
  },
  min: {
    value: 0,
    message: `Must be at least 0.`
  },
  validate: {
    nan: getIsNanError("Must be a number.")
  }
};
const FieldConfig = {
  loanAmount: {
    name: "loanAmount",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  rate: {
    name: "rate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  term: {
    name: "term",
    config: {
      required: "Required",
      ...COMMON_TERM_CONFIG
    }
  },
  originationYear: {
    name: "originationYear",
    config: {
      required: "Required",
      min: {
        value: 1980,
        message: "Must be at least 1980."
      },
      validate: {
        isGreaterThanCurrentYearError: function isGreaterThanCurrentYearError(value) {
          const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
          const error = value > currentYear ? `Must be ${currentYear} at most.` : null;
          return error;
        },
        nan: getIsNanError("Must be a number.")
      }
    }
  },
  newLoanAmount: {
    name: "newLoanAmount",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  newTerm: {
    name: "newTerm",
    config: {
      required: "Required",
      ...COMMON_TERM_CONFIG
    }
  },
  newRate: {
    name: "newRate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  fees: {
    name: "fees",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  cashOut: {
    name: "cashOut",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  rollFees: {
    name: "rollFees"
  }
};
function useLastValidRefinanceValues() {
  const form = useFormContext();
  return useLastValidValues(form, getRefinanceValues);
}
function getRefinanceValues(lastValidFields) {
  const calculations = refinance.default(lastValidFields);
  const values = {
    fields: lastValidFields,
    calculations
  };
  return values;
}
function useRefinanceColors() {
  const lifetimeSavingsColor = token.var("colors.bg.accent.green.hero");
  const costsColor = token.var("colors.border.action.critical.hero.default");
  const infoPointColor = token.var("colors.bg.accent.brand.hero");
  return {
    lifetimeSavingsColor,
    costsColor,
    infoPointColor
  };
}
function abbr(num) {
  let n = parseInt(String(num), 10);
  let v;
  let s;
  if (Number.isNaN(n)) {
    n = 0;
  }
  const an = Math.abs(n);
  if (an >= 1e9) {
    v = parseInt(String(n / 1e8), 10) / 10;
    s = "B";
  } else if (an >= 1e6) {
    v = parseInt(String(n / 1e5), 10) / 10;
    s = "M";
  } else if (an >= 1e3) {
    v = parseInt(String(n / 100), 10) / 10;
    s = "K";
  } else {
    v = parseInt(String(n), 10);
    s = "";
  }
  if (v >= 10) {
    v = parseInt(String(v), 10);
  }
  return v + s;
}
const REFINANCE_CALC = "refinance_calc";
const TOOLTIP = "tooltip";
const refinancePopoverCmsInfo = {
  block_type_cd: "modal_dialog",
  block_id: REFINANCE_CALC,
  block_displayed_txt: "Calculator disclaimer"
};
export {
  FieldConfig,
  REFINANCE_CALC,
  TOOLTIP,
  abbr,
  refinancePopoverCmsInfo,
  useLastValidRefinanceValues,
  useRefinanceColors
};
